// Página oculta de edição com recursos experimentais de IA.
// Rota: /apreciacoes/:id/editarV2 (sem link em menu).
// Diferenças vs apreciacao-edit.tsx:
//   1) Botão "Copiar de outra apreciação" acima do cabeçalho.
//   2) Painel "Gerar partes com IA" acima da tabela de partes.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-hook';
import { useSidebar } from '@/contexts/sidebar-context';
import { useI18n } from '@/contexts/i18n-hook';
import { apiService } from '@/services/api';
import { TabelaPartes } from '@/components/apreciacao/tabela-partes';
import { PreviewLatex } from '@/components/apreciacao/preview-latex';
import { ModalEditarParte } from '@/components/apreciacao/modal-editar-parte';
import { NavegacaoEdicaoMaquina } from '@/components/apreciacao/navegacao-edicao-maquina';
import { CabecalhoIdentificacao } from '@/components/apreciacao/cabecalho-identificacao';
import { Etapa1DadosMaquina } from '@/components/apreciacao/etapa1-dados-maquina';
import { ResizableDivider } from '@/components/apreciacao/resizable-divider';
import { useApreciacaoLock } from './hooks/useApreciacaoLock';
import { useApreciacaoData } from './hooks/useApreciacaoData';
import { useApreciacaoSave } from './hooks/useApreciacaoSave';
import { useApreciacaoPartes } from './hooks/useApreciacaoPartes';
import { salvarParte } from './hooks/handleSalvarParte';
import { LockedScreen } from './components/LockedScreen';
import { LoadingScreen } from './components/LoadingScreen';
import { EditHeader } from './components/EditHeader';
import { CopyFromApreciacaoModal } from './v2/CopyFromApreciacaoModal';
import { AIGeneratePartesPanel } from './v2/AIGeneratePartesPanel';

export function ApreciacaoEditV2() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { setSidebarCollapsed } = useSidebar();
  const [activeTab, setActiveTab] = useState<'form' | 'preview'>('form');
  const [etapaAtiva, setEtapaAtiva] = useState<'dados' | 'partes'>('dados');
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const [copiarModalAberto, setCopiarModalAberto] = useState(false);

  const {
    apreciacao,
    dados,
    riscos,
    loading: dataLoading,
    setDados,
    setRiscos,
    carregarApreciacao,
    limparDados,
  } = useApreciacaoData();

  const {
    lockInfo,
    lockLoading,
    lockInicializado,
    erroLock,
    removerLock,
    renovar,
    eMeuLock,
    bloqueadoPorOutro,
    inicializarLock,
  } = useApreciacaoLock({
    apreciacaoId: id,
    usuarioId: user?.id || '',
    onLockBlocked: () => { limparDados(); },
  });

  const { saving, salvar } = useApreciacaoSave({
    apreciacaoId: id,
    dados,
    riscos,
    renovarLock: renovar,
    bloqueadoPorOutro,
    erroLock,
    onSalvo: async () => { if (id) await carregarApreciacao(id); },
  });

  const {
    modalParteAberto,
    parteSelecionada,
    handleEditarParte,
    handleFecharModal,
    handleDeletarParte,
    handleDuplicarParte,
    handleAdicionarParte,
    handleReordenarPartes,
  } = useApreciacaoPartes({ apreciacaoId: id, riscos, setRiscos });

  const handleSalvarParte = async (dadosParte: any) => {
    if (!id || !parteSelecionada) return;
    try {
      await salvarParte({
        id,
        parteSelecionada,
        dadosParte,
        riscos,
        setRiscos,
        onFechar: handleFecharModal,
        t,
      });
    } catch (error) {
      console.error('Erro ao salvar parte:', error);
      throw error;
    }
  };

  const [paginaUltimaParte, setPaginaUltimaParte] = useState<number>(14);

  useEffect(() => {
    setSidebarCollapsed(true);
    return () => {
      setSidebarCollapsed(false);
      if (id) {
        removerLock().catch((err) => { console.warn('Erro ao remover lock no cleanup:', err); });
      }
    };
  }, [setSidebarCollapsed, id, removerLock]);

  const inicializadoRef = useRef<string | null>(null);
  useEffect(() => {
    if (id && user?.id && inicializadoRef.current !== id) {
      inicializadoRef.current = id;
      (async () => {
        const lockOk = await inicializarLock();
        if (lockOk) await carregarApreciacao(id);
      })();
    }
    return () => { if (id && inicializadoRef.current === id) inicializadoRef.current = null; };
  }, [id, user?.id]);

  const handleChange = (campo: string, valor: any) => {
    if (campo === 'maquina') {
      setDados((prev: any) => ({ ...prev, maquina: valor }));
    } else if (campo === 'ativo') {
      setDados((prev: any) => ({ ...prev, ativo: valor, ativoId: valor?.id || null }));
    } else {
      setDados((prev: any) => ({ ...prev, [campo]: valor }));
    }
    if (eMeuLock()) renovar();
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    await salvar();
  };

  const handleSucessoCopia = async () => {
    if (id) await carregarApreciacao(id);
  };

  const handlePartesGeradas = async () => {
    if (id) await carregarApreciacao(id);
  };

  const [detalhandoIA, setDetalhandoIA] = useState<string | null>(null);
  const [baixandoZip, setBaixandoZip] = useState<string | null>(null);

  const handleBaixarImagens = useCallback(async (parte: string) => {
    if (!id || baixandoZip) return;
    setBaixandoZip(parte);
    try {
      const blob = await apiService.downloadFotosParte(id, parte);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = parte.replace(/[^a-zA-Z0-9À-ÿ\-_]/g, '_').slice(0, 80) || 'parte';
      link.download = `fotos_${safe}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(
        t('apreciacoes.editar.v2.erroBaixarImagens', {
          detalhe: e?.message || t('apreciacoes.erroDesconhecido'),
        })
      );
    } finally {
      setBaixandoZip(null);
    }
  }, [id, baixandoZip, t]);

  const handleDetalharIA = useCallback(async (parte: string, riscosDaParte: any[]) => {
    if (!id || detalhandoIA) return;
    const primeiroRisco = riscosDaParte[0];
    if (!primeiroRisco?.id) return;

    setDetalhandoIA(parte);
    try {
      // O backend lê as fotos do disco a partir do risco
      const resp = await apiService.aiRedetalharParte(id, primeiroRisco.id);

      if (resp.success) {
        await carregarApreciacao(id);
      } else {
        alert(
          t('apreciacoes.editar.v2.erroDetalharIA', {
            detalhe: resp.message || t('apreciacoes.erroDesconhecido'),
          })
        );
      }
    } catch (e: any) {
      alert(
        t('apreciacoes.editar.v2.erroDetalharIA', {
          detalhe: e?.message || t('apreciacoes.erroDesconhecido'),
        })
      );
    } finally {
      setDetalhandoIA(null);
    }
  }, [id, detalhandoIA, carregarApreciacao, t]);

  const loading = dataLoading || lockLoading || !lockInicializado;

  if (erroLock) return <LockedScreen erroLock={erroLock} apreciacaoId={id} />;
  if (loading)
    return (
      <LoadingScreen
        mensagem={
          lockLoading
            ? t('apreciacoes.editar.verificandoBloqueio')
            : t('apreciacoes.editar.carregandoApreciacao')
        }
      />
    );
  if (!dados) return <LoadingScreen mensagem={t('apreciacoes.editar.carregandoDados')} />;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="flex-shrink-0 p-6 bg-white dark:bg-schmersal-gray-darker border-b border-schmersal-gray-light dark:border-schmersal-gray-dark">
        <EditHeader
          titulo={dados.titulo || t('apreciacoes.editar.tituloFallback', { numero: dados.numero || '' })}
          numero={dados.numero}
          lockInfo={lockInfo}
          eMeuLock={eMeuLock()}
          bloqueadoPorOutro={bloqueadoPorOutro()}
          onSave={(e) => handleSalvar(e || { preventDefault: () => { } } as React.FormEvent)}
          saving={saving}
          disabled={bloqueadoPorOutro() && !eMeuLock()}
        />
        <div className="mt-2 text-xs text-purple-700 dark:text-purple-300 font-semibold">
          {t('apreciacoes.editar.v2.badge')}
        </div>
      </div>

      {bloqueadoPorOutro() && !eMeuLock() && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <strong>{t('apreciacoes.aviso.atencao')}</strong> {t('apreciacoes.editar.aviso.bloqueada')}
          </p>
        </div>
      )}

      <div className="flex-shrink-0">
        <NavegacaoEdicaoMaquina etapaAtiva={etapaAtiva} onEtapaChange={setEtapaAtiva} />
      </div>

      <div className="lg:hidden flex border-b border-gray-200 mx-6">
        <button
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 ${activeTab === 'form' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('form')}
        >
          {t('apreciacoes.editar.tab.edicao')}
        </button>
        <button
          className={`flex-1 py-3 text-sm font-medium text-center border-b-2 ${activeTab === 'preview' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('preview')}
        >
          {t('apreciacoes.editar.tab.preview')}
        </button>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div
          className={`flex-shrink-0 overflow-y-auto ${activeTab === 'preview' ? 'hidden lg:block' : ''}`}
          style={{ width: `${leftPanelWidth}%` }}
        >
          <div className="p-6">
            <form onSubmit={handleSalvar} className="space-y-6">
              {etapaAtiva === 'dados' ? (
                <>
                  <div className="rounded-lg border-2 border-dashed border-blue-400 dark:border-blue-600 bg-blue-50/60 dark:bg-blue-950/20 p-4 flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-base font-bold text-blue-900 dark:text-blue-200">
                        {t('apreciacoes.editar.v2.copiarTitulo')}
                      </h3>
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        {t('apreciacoes.editar.v2.copiarSubtitulo')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCopiarModalAberto(true)}
                      disabled={bloqueadoPorOutro() && !eMeuLock()}
                      className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-60 whitespace-nowrap"
                    >
                      {t('apreciacoes.editar.v2.escolherApreciacao')}
                    </button>
                  </div>

                  <CabecalhoIdentificacao dados={dados} onChange={handleChange} modoEdicao={true} />
                  <Etapa1DadosMaquina dados={dados} onChange={handleChange} modoEdicao={true} apreciacaoId={id} />
                </>
              ) : (
                <div className="space-y-4">
                  {id && (
                    <AIGeneratePartesPanel
                      apreciacaoId={id}
                      onPartesGeradas={handlePartesGeradas}
                    />
                  )}
                  <TabelaPartes
                    riscos={riscos}
                    onAdicionar={handleAdicionarParte}
                    onEditar={(parte, riscosParte) => {
                      const partesMap = new Map<string, { numero: number; parte: string; riscos: any[] }>();
                      riscos.forEach((risco) => {
                        const parteNome = risco.parte || 'Sem parte definida';
                        if (!partesMap.has(parteNome)) {
                          partesMap.set(parteNome, { numero: partesMap.size + 1, parte: parteNome, riscos: [] });
                        }
                        partesMap.get(parteNome)!.riscos.push(risco);
                      });
                      const dadosParte = partesMap.get(parte);
                      if (dadosParte) {
                        setPaginaUltimaParte(14 + (dadosParte.numero - 1) * 3);
                        handleEditarParte(parte, riscosParte, dadosParte.numero);
                      }
                    }}
                    onDeletar={handleDeletarParte}
                    onDuplicar={handleDuplicarParte}
                    onReordenar={handleReordenarPartes}
                    onDetalharIA={handleDetalharIA}
                    onBaixarImagens={handleBaixarImagens}
                    modoEdicao={true}
                  />
                </div>
              )}
            </form>
          </div>
        </div>

        <div className="hidden lg:block flex-shrink-0">
          <ResizableDivider onResize={setLeftPanelWidth} initialLeftWidth={50} />
        </div>

        <div
          className={`flex-1 flex flex-col overflow-hidden ${activeTab === 'form' ? 'hidden lg:flex' : ''}`}
          style={{ width: `${100 - leftPanelWidth}%` }}
        >
          <PreviewLatex
            apreciacao={apreciacao}
            dados={dados}
            riscos={riscos}
            paginaUltimaParte={paginaUltimaParte}
          />
        </div>
      </div>

      {modalParteAberto && parteSelecionada && (
        <ModalEditarParte
          aberto={modalParteAberto}
          parte={parteSelecionada.parte}
          riscos={parteSelecionada.riscos}
          numeroParte={parteSelecionada.numero}
          onFechar={handleFecharModal}
          onSalvar={handleSalvarParte}
          descricaoMaquina={dados?.maquina?.descricao || ''}
          apreciacaoId={id}
        />
      )}

      {id && (
        <CopyFromApreciacaoModal
          aberto={copiarModalAberto}
          projetoId={apreciacao?.projetoId}
          destinoId={id}
          onFechar={() => setCopiarModalAberto(false)}
          onSucesso={handleSucessoCopia}
        />
      )}
    </div>
  );
}
